import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import countryData from 'src/assets/media/json/countries.json';

interface PhoneCountry {
  name: string;
  iso2: string;
  phoneCode: string;
}

@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PhoneNumberInputComponent),
    multi: true
  }]
})
export class PhoneNumberInputComponent implements ControlValueAccessor {
  @Input() inputId = 'phoneNumber';
  @Input() placeholder = 'Enter phone number';
  @Input() invalid = false;

  readonly countries = (countryData as PhoneCountry[])
    .filter((country) => country.iso2 && country.phoneCode)
    .sort((left, right) => left.name.localeCompare(right.name));
  private readonly countriesByCallingCode = [...this.countries].sort(
    (left, right) => this.getCallingCodeDigits(right).length - this.getCallingCodeDigits(left).length
  );

  selectedCountry: PhoneCountry | null = this.countries[0] ?? null;
  phoneNumber = '';
  disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    const normalizedValue = value?.trim() || '';
    const digits = normalizedValue.replace(/\D/g, '');
    const matchedCountry = normalizedValue.startsWith('+')
      ? this.countriesByCallingCode.find((country) => digits.startsWith(this.getCallingCodeDigits(country)))
      : undefined;

    this.selectedCountry = matchedCountry ?? this.countries[0] ?? null;
    this.phoneNumber = matchedCountry
      ? digits.slice(this.getCallingCodeDigits(matchedCountry).length)
      : digits;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onCountryChange(country: PhoneCountry | null): void {
    this.selectedCountry = country;
    this.updateValue();
    this.onTouched();
  }

  onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    const callingCode = this.selectedCountry ? this.getCallingCodeDigits(this.selectedCountry) : '';
    this.phoneNumber = callingCode && digits.startsWith(callingCode)
      ? digits.slice(callingCode.length)
      : digits;
    input.value = this.phoneNumber;
    this.updateValue();
  }

  markAsTouched(): void {
    this.onTouched();
  }

  // Keep the full international number within E.164's 15-digit limit.
  get maxPhoneNumberLength(): number {
    const codeLength = this.selectedCountry ? this.getCallingCodeDigits(this.selectedCountry).length : 0;
    return Math.max(0, 15 - codeLength);
  }

  private updateValue(): void {
    const callingCode = this.selectedCountry ? this.getCallingCodeDigits(this.selectedCountry) : '';
    this.onChange(callingCode && this.phoneNumber ? `${callingCode}${this.phoneNumber}` : '');
  }

  private getCallingCodeDigits(country: PhoneCountry): string {    
    return country.phoneCode.replace(/\D/g, '');
  }
}